/**
 * Statsig analytics: autocapture (pageviews/clicks), session replay, and the
 * explicit booking-funnel events used to analyse conversion drop-off.
 *
 * A single module-level client (rather than useClientAsyncInit) so any code —
 * component or not — can log events via logStatsigEvent() without hooks, and so
 * the app renders immediately instead of waiting for Statsig to initialise
 * (events logged before init completes are queued and flushed automatically).
 *
 * The client key is a PUBLIC key (it ships in the browser bundle by design).
 * Identity: Statsig's auto-generated stableID tracks anonymous visitors across
 * the funnel; when a customer signs in we attach their uid via setStatsigUser().
 *
 * ── Booking funnel (Statsig → Metrics → Funnels), in order ────────────────────
 *   booking_postcode_submitted   step 0  (metadata.available separates "no
 *                                         coverage" exits from real drop-off)
 *   booking_category_submitted   step 1
 *   booking_contact_submitted    step 2  (phone+email valid; before OTP)
 *   booking_otp_verified         step 2b (only when an existing account forced OTP)
 *   booking_address_submitted    step 3
 *   booking_job_created          step 4  (job exists server-side; metadata.source:
 *                                         form | chat - EmergencyForm and
 *                                         ChatPanel's BookingConfirmCard both log
 *                                         this same event, so a chat-originated
 *                                         booking isn't invisible to this funnel)
 *   booking_payment_viewed       payment screen rendered (paywall-on only - see below)
 *   booking_payment_succeeded    fee paid (PurchaseComplete page; paywall-on only)
 *   booking_quotes_viewed        quotes shown after the search
 *   booking_quote_selected       plumber chosen — full conversion, both variants
 * Failure markers (not funnel steps): booking_job_create_failed, booking_search_failed
 *
 * outreachChannel: the job-status events logged from JobStatus.tsx
 * (booking_payment_viewed / booking_quotes_viewed / booking_search_failed /
 * booking_quote_selected) also carry metadata.outreachChannel ('call' | 'sms')
 * - the SMS-vs-call plumber outreach experiment's variant, stamped on the job
 * by the server when its search started. Segment on it the same way as
 * paywallVariant below to compare quotes_viewed / quote_selected rates.
 *
 * paywallVariant (issue #40): booking_job_created onwards all carry
 * metadata.paywallVariant ('paywall_on' | 'paywall_off') so the two variants'
 * conversion rate through the SAME shared spine (job_created → quotes_viewed →
 * quote_selected) can be filtered/compared in Statsig, rather than needing a
 * second, parallel funnel. It's job-level truth (derived from whether the
 * job actually has a payment - see JobStatus.tsx), not the live config toggle:
 * a job keeps whatever variant applied when it was created even if the toggle
 * flips later. booking_payment_viewed/succeeded only exist at all for the
 * paywall-on path (the free path has no payment step to view or succeed at) -
 * that's expected, not a gap, which is exactly why the cross-variant
 * comparison funnel is built on the steps both variants actually share.
 *
 * ── Chat funnel (issue #40 - "how much does live chat actually get used") ─────
 *   chat_opened                  chat panel opened (metadata.page). Carries
 *                                 metadata.source:'deeplink' when it was
 *                                 opened by ?chat=1 rather than by tapping an
 *                                 on-page affordance - that's how the customer
 *                                 phone line's SMS reply arrives (issue #79),
 *                                 so it's the one entry point whose conversion
 *                                 can be read separately from the rest.
 *   chat_quick_reply_clicked     a one-tap opener under the greeting (value:
 *                                 leak | boiler | drain | other). Compare with
 *                                 chat_message_sent to see whether chips beat
 *                                 typing as the first move.
 *   chat_message_sent            each customer message (value: message number
 *                                 in the conversation, metadata.messageNumber)
 *   chat_human_support_requested request_human_support tool succeeded - a
 *                                 real ticket reached the support Discord
 *                                 channel (value: ticket ref)
 * A chat-originated booking is NOT a separate event - it's the same
 * booking_job_created/booking_quote_selected steps above, tagged source:'chat',
 * so chat's actual booking impact is directly comparable to the form's.
 *
 * ── Plumber-profile entry (SEO landings), feeds the main funnel above ─────────
 * The plumber-specific direct-booking wizard (/book/:id, direct_booking_*
 * events) was retired 2026-08-26 — every profile CTA now sends the visitor to
 * the homepage emergency form, so profile traffic continues into the
 * booking_* funnel from booking_postcode_submitted onwards.
 *   plumber_profile_viewed     profile page rendered (metadata.page:
 *                              directory-profile | plumber-profile)
 *   plumber_book_cta_clicked   profile CTA tapped (metadata.placement:
 *                              mobile-sticky-bar | sidebar | cta-card)
 *   plumber_text_cta_clicked   "Text us now" tapped on a profile (metadata.placement:
 *                              sidebar | cta-card). The customer line is SMS-only (issue
 *                              #79), so this replaced plumber_call_cta_clicked, which only
 *                              ever existed for a day. Watch it against inbound texts on the
 *                              line: it is how you tell whether people tapping it are doing
 *                              so thinking they are texting the plumber (404a96d).
 */
import { StatsigClient } from '@statsig/js-client'

const STATSIG_CLIENT_KEY = 'client-H1Nil5Qf8n486y8LG4zqPeCknEkzsMgDuKik7kxzKt5'

// Don't record the prerenderer (Playwright) or other headless visitors — they'd
// pollute pageview/funnel data with a fake visit for every prerendered page.
const isBot = typeof navigator !== 'undefined' && navigator.webdriver

export const statsigClient = new StatsigClient(
  STATSIG_CLIENT_KEY,
  {}, // anonymous; stableID identifies the visitor until they sign in
  { environment: { tier: import.meta.env.PROD ? 'production' : 'development' } },
)

if (!isBot) {
  statsigClient.initializeAsync().catch(() => { /* analytics must never break the app */ })
  // Autocapture + session replay are heavy (rrweb); load them in a separate
  // lazy chunk after init so they never sit in the LCP-critical entry bundle.
  Promise.all([import('@statsig/web-analytics'), import('@statsig/session-replay')])
    .then(([wa, sr]) => {
      wa.runStatsigAutoCapture(statsigClient)
      sr.runStatsigSessionReplay(statsigClient)
    })
    .catch(() => { /* analytics must never break the app */ })
}

/** Log an event; safe to call from anywhere, never throws, no-ops for bots. */
export function logStatsigEvent(
  eventName: string,
  value?: string | number,
  metadata?: Record<string, string>,
): void {
  if (isBot) return
  try {
    statsigClient.logEvent(eventName, value, metadata)
  } catch { /* ignore */ }
}

/** Attach/detach the signed-in customer's id (keeps the same stableID). */
export function setStatsigUser(userID: string | undefined): void {
  if (isBot) return
  statsigClient.updateUserAsync({ ...(userID ? { userID } : {}) }).catch(() => { /* ignore */ })
}
